import React, { useEffect } from "react";
import "./UserfulConfirmationDialog.scss";
import Button from "../button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { faMultiply } from "@fortawesome/pro-regular-svg-icons";

interface IProps {
    show: boolean;
    gap?: number; // Gap between elements in the body
    spinningElement?: boolean;
    spin?: boolean; // If true, the icon will spin
    title?: string; // Title of the dialog
    message?: string; // Message of the dialog
    varient?: "primary" | "secondary" | "danger" | "success" | "warning" | "dark";
    icon?: { icon: any; color?: string }; // Default icon is faWarning
    missingTranslaiton?: boolean; // If true, the title and message will not be translated
    noCloseButton?: boolean; // If true, the close button will not be displayed
    noCancelButton?: boolean; // hide cancel button in footer
    replaceBody?: React.ReactNode | undefined | "NO"; // Please use <p></p> for text, strong for highlighted text
    replaceFooter?: React.ReactNode | undefined | "NO"; // Please use <Button></Button> for buttons
    cancelButtonText?: string;
    confirmButtonText?: string;
    onCancel?: () => void;
    onConfirm?: () => void;
    disabled?: boolean;
    // Additional functions can be insert into replaceFooter fo each button
}

export default function UserfulConfirmationDialog(props: IProps) {
    const { t } = useTranslation();

    const onCancel = () => {
        console.debug("UserfulConfirmationDialog onCancel");
    };

    const onConfirm = () => {
        console.debug("UserfulConfirmationDialog onConfirm");
    };

    useEffect(() => {
        // Only wire keyboard shortcuts when using the default footer
        if (!props.show || props.replaceFooter !== undefined) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                if (props.onCancel) props.onCancel();
                else onCancel();
            }

            if (event.key === "Enter") {
                if (props.disabled) return;
                if (props.onConfirm) props.onConfirm();
                else onConfirm();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [props.show, props.replaceFooter, props.disabled, props.onCancel, props.onConfirm]);

    return (
        <Modal className="noselect" show={props.show} centered>
            <div className="userful-exclusive-confirmation-header">
                <div className="userful-exclusive-confirmation-header-text">
                    {props.icon && (
                        <FontAwesomeIcon
                            spin={props.spin}
                            className={`${props.varient ? props.varient : ""}-icon-color`}
                            icon={props.icon.icon}
                            style={{ color: props.icon.color ? props.icon.color : "" }}
                        />
                    )}
                    {props.spinningElement && (
                        <img
                            className="userful-exclusive-confirmation-icon-spinning"
                            src="./images/failover_loading.svg"
                        />
                    )}
                    <h3>{props.missingTranslaiton ? props.title : t(props.title)}</h3>
                </div>
                {!props.noCloseButton && (
                    <Button
                        variant="secondary"
                        iconOnly
                        onClick={() => {
                            if (props.onCancel) props.onCancel();
                            else onCancel();
                        }}
                    >
                        <FontAwesomeIcon icon={faMultiply} />
                    </Button>
                )}
            </div>
            {props.replaceBody !== "NO" && (
                <div
                    className="userful-exclusive-confirmation-body"
                    style={{ gap: props.gap ? props.gap : 16, marginBottom: props.replaceFooter === "NO" ? 0 : 8 }}
                >
                    {props.replaceBody ? (
                        props.replaceBody
                    ) : (
                        <p>{props.missingTranslaiton ? props.message : t(props.message)}</p>
                    )}
                </div>
            )}
            {props.replaceFooter !== "NO" && (
                <div className="userful-exclusive-confirmation-footer">
                    {props.replaceFooter ? (
                        props.replaceFooter
                    ) : (
                        <>
                            {!props.noCancelButton && (
                                <Button variant="secondary" onClick={props.onCancel ? props.onCancel : onCancel}>
                                    {props.cancelButtonText || t("Shared.cancel")}
                                </Button>
                            )}
                            {props.varient && (
                                <Button
                                    variant={props.varient}
                                    onClick={props.onConfirm ? props.onConfirm : onConfirm}
                                    disabled={props.disabled}
                                >
                                    {props.confirmButtonText || t("Shared.confirm")}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}
        </Modal>
    );
}
